<template>
  <div>
    <CCard>
      <CCardHeader>
        <slot name="header">
          <CIcon :name="tableIcon" /> {{ pageTitle }}
          <CButtonGroup
            class="float-right"
          >
            <CButton size="sm" color="primary" @click="onAddNew">
              <CIcon name="cil-plus" /> Add New
            </CButton>
            <CButton size="sm" color="success" @click="onRefresh">
              <CIcon name="cil-action-redo" /> Refresh
            </CButton>
          </CButtonGroup>
        </slot>
      </CCardHeader>
      <CCardBody>
        <list-pagination
          :pageData="pageData"
          :perPageSelectData="perPageSelect"
          :entity="entity"
          containerClass="header-pagination"
          @pageChange="changePage"
        />
        <div class="lists-table">
          <CDataTable
            :hover="true"
            :striped="true"
            :border="true"
            :small="true"
            :fixed="true"
            :items="lists"
            :fields="fields"
            :dark="false"
            :loading="fetching"
            :clickableRows="true"
            addTableClasses="header-fixed"
            :sorter="{ external: true }"
            :sorterValue="sorterValue"
            @row-clicked="rowClicked"
            @update:sorter-value="updateSort"
          >
            <template #roles="{item}">
              <td class="text-center">
                <CBadge v-start-case color="primary" v-for="role in item.roles" :key="role.id">
                  {{ role.name }}
                </CBadge>
              </td>
            </template>
            <template #email_verified_at="{item}">
              <td>
                <span v-if="item.verified_at">{{ item.verified_at }}</span>
                <CBadge v-if="!item.verified_at" color="danger">Not Verified</CBadge>
              </td>
            </template>
            <template #subscriptions={item}>
              <td v-if="hasSubscription(item)" class="text-center">
                <span class="d-block">{{ item.total_subscriptions }}</span>
                <CLink @click="() => showSubscriptions(item)">(View Subscriptions)</CLink>
              </td>
              <td v-else class="text-center">
                N/A
              </td>
            </template>
            <template #action="{item}">
              <td>
                <CButtonGroup size="sm">
                  <CButton color="primary" @click="() => onEditItem(item)">Edit</CButton>
                  <CButton v-if="item.deletable" color="danger" @click="() => onDeleteItem(item)">Delete</CButton>
                </CButtonGroup>
              </td>
            </template>
          </CDataTable>
        </div>
        <list-pagination
          :pageData="pageData"
          :perPageSelectData="perPageSelect"
          :entity="entity"
          containerClass="footer-pagination mt-2"
          @pageChange="changePage"
        />
      </CCardBody>
    </CCard>
    <Create
      v-if="newData.show"
      :key="newData.key"
    />
    <Update
      v-if="!!editData.data"
      :key="editData.key"
    />
    <Delete
      v-if="!!deleteData.data"
      :key="deleteData.key"
    />
    <Detail
      v-if="detailData.data"
      :key="detailData.key"
    />
    <UserSubscription
      v-if="userSubscriptionsData.show"
      :key="userSubscriptionsData.key"
    />
  </div>
</template>

<script>
import startCase from 'lodash/startCase'
import { mapActions, mapState } from 'vuex'
import store from './store'

import Create from './components/Create'
import Update from './components/Update'
import Delete from './components/Delete'
import Detail from './components/Detail'
import UserSubscription from './components/UserSubscription/UserSubscription'

import { entity, tableIcon } from './entity'
import tableFields from './tableFields'

export default {
  name: `${startCase(entity)}Lists`,
  title: startCase(entity),
  props: {
    showNew: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Create,
    Update,
    Delete,
    Detail,
    UserSubscription
  },
  data () {
    return {
      entity,
      tableIcon,
      pageTitle: startCase(entity),
      fields: tableFields,
      perPageSelect: {
        label: `${startCase(entity)} Per Page:`,
        options: [10, 15, 25, 50, 100],
        external: false
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.session.loggedUser,
      lists: state => state[entity].lists,
      pageData: state => state[entity].pageData,
      sortData: state => state[entity].sortData,
      fetching: state => state[entity].isFetching,
      newData: state => state[entity].new,
      editData: state => state[entity].edit,
      deleteData: state => state[entity].delete,
      detailData: state => state[entity].detail,
      userSubscriptionsData: state => state[entity].userSubscriptions
    }),
    sorterValue () {
      const { column, direction } = this.sortData
      return {
        column,
        asc: direction === 'asc'
      }
    }
  },
  methods: {
    ...mapActions({
      loadLists: `${entity}/loadLists`,
      changePage: `${entity}/changePage`,
      changeSort: `${entity}/changeSort`
    }),
    onAddNew () {
      this.$store.commit(`${entity}/ADD_NEW`, true)
    },
    onRefresh () {
      this.loadLists()
    },
    onEditItem (item) {
      this.$store.commit(`${entity}/EDIT_DATA`, item)
    },
    onDeleteItem (item) {
      this.$store.commit(`${entity}/DELETE_DATA`, item)
    },
    onPageChange (currentPage) {
      this.changePage({ currentPage })
    },
    onPaginationChange (perPage) {
      this.changePage({ perPage })
    },
    rowClicked (item, idx, key) {
      if (!['action', 'subscriptions'].includes(key)) {
        this.$store.commit(`${entity}/DETAIL_DATA`, item)
      }
    },
    updateSort ({ asc, column }) {
      this.changeSort({ column, direction: asc ? 'asc' : 'desc' })
    },
    hasSubscription (item) {
      return item.roles.find(e => e.name === 'user')
    },
    showSubscriptions (user) {
      this.$store.commit(`${entity}/SHOW_USER_SUBSCRIPTIONS`, { user })
    }
  },
  beforeMount () {
    if (!this.$store.state[entity]) {
      this.$store.registerModule(entity, store)
    }
  },
  mounted () {
    this.loadLists()
  }
}
</script>
